import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import OrangeMoneyPayment from './OrangeMoneyPayment';
import ParentPaymentDialog from './ParentPaymentDialog';
import BulkInvoiceDialog from './BulkInvoiceDialog';

// Icons
import { CreditCard, FileText, Download, Plus, Calendar, CheckCircle, XCircle, Clock, AlertTriangle, Users, BellRing } from 'lucide-react';
import { InvoiceReminderDialog } from './InvoiceReminderDialog';
import { ReminderSettingsCard } from './ReminderSettingsCard';
import { FinanceExportPanel } from './FinanceExportPanel';
import { PaymentsImportPanel } from './PaymentsImportPanel';
import { CollectionDashboard } from './CollectionDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BillingManagement = ({ user }) => {
  const [invoices, setInvoices] = useState([]);
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('invoices');
  const [statusFilter, setStatusFilter] = useState(() => new URLSearchParams(window.location.search).get('status') || 'ALL');
  const [reminderInvoice, setReminderInvoice] = useState(null);
  
  // Dialog states
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [bulkInvoiceDialogOpen, setBulkInvoiceDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  const [invoiceForm, setInvoiceForm] = useState({
    student_id: '',
    title: '',
    description: '',
    total_amount: '',
    due_date: '',
    periodicity: 'MONTHLY'
  });

  const [paymentForm, setPaymentForm] = useState({
    method: 'CARD',
    card_number: '',
    expiry_date: '',
    cvv: '',
    cardholder_name: '',
    phone_number: ''
  });

  const invoiceStatuses = [
    { value: 'DRAFT', label: 'Brouillon', color: 'badge-secondary', icon: FileText },
    { value: 'SENT', label: 'Envoyée', color: 'badge-primary', icon: Clock },
    { value: 'PAID', label: 'Payée', color: 'badge-success', icon: CheckCircle },
    { value: 'OVERDUE', label: 'En retard', color: 'badge-danger', icon: AlertTriangle },
    { value: 'CANCELLED', label: 'Annulée', color: 'badge-secondary', icon: XCircle },
    { value: 'PENDING', label: 'Non échue', color: 'badge-primary', icon: Clock },
    { value: 'PARTIAL', label: 'Partiellement payée', color: 'badge-warning', icon: Clock }
  ];

  const periodicityOptions = [
    { value: 'MONTHLY', label: 'Mensuelle' },
    { value: 'QUARTERLY', label: 'Trimestrielle' },
    { value: 'BIANNUAL', label: 'Semestrielle' },
    { value: 'ANNUAL', label: 'Annuelle' }
  ];

  const paymentMethods = [
    { value: 'CARD', label: 'Carte bancaire' },
    { value: 'TRANSFER', label: 'Virement bancaire' },
    { value: 'CHECK', label: 'Chèque' },
    { value: 'CASH', label: 'Espèces' },
    { value: 'ORANGE_MONEY', label: 'Orange Money' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Recharger les paiements quand on accède à l'onglet "payments"
  useEffect(() => {
    if (activeTab === 'payments') {
      fetchPaymentsData();
    }
  }, [activeTab]);

  const fetchPaymentsData = async () => {
    try {
      const response = await axios.get(`${API}/payments`);
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchInitialData = async () => {
    try {
      const promises = [
        axios.get(`${API}/invoices`),
        ['PARENT', 'STUDENT'].includes(user.role)
          ? Promise.resolve({ data: {} })
          : axios.get(`${API}/financial/statistics`),
        axios.get(`${API}/payments`)
      ];

      if (user.role !== 'PARENT' && user.role !== 'STUDENT') {
        // Utiliser /api/students au lieu de /api/users pour avoir le student_id correct
        promises.push(axios.get(`${API}/students`));
      }

      const results = await Promise.all(promises);

      setInvoices(results[0].data);
      setStatistics(results[1].data);
      setPayments(results[2].data);
      
      if (results[3]) {
        setStudents(results[3].data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/invoices`, {
        ...invoiceForm,
        total_amount: parseFloat(invoiceForm.total_amount),
        due_date: new Date(invoiceForm.due_date).toISOString()
      });

      setInvoices(prev => [response.data, ...prev]);
      toast.success('Facture créée avec succès');
      resetInvoiceForm();
      setInvoiceDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la création de la facture');
      console.error('Error creating invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (paymentForm.method === 'ORANGE_MONEY') {
        // Orange Money payment
        const paymentRequest = {
          invoice_id: selectedInvoice.id,
          amount: selectedInvoice.total_amount,
          phone_number: paymentForm.phone_number,
          payment_method: 'orange_money'
        };

        const response = await axios.post(`${API}/payment/initiate`, paymentRequest);
        
        if (response.data.status === 'SUCCESSFUL') {
          // Update invoice in local state
          setInvoices(prev => 
            prev.map(inv => 
              inv.id === selectedInvoice.id 
                ? { ...inv, status: 'PAID', payment_date: new Date().toISOString(), payment_method: paymentForm.method }
                : inv
            )
          );
          toast.success(`Paiement Orange Money réussi! Transaction: ${response.data.transaction_id}`);
        } else {
          toast.error('Échec du paiement Orange Money');
        }
      } else {
        // Traditional payment processing (existing logic)
        await axios.put(`${API}/invoices/${selectedInvoice.id}/pay`, paymentForm);

        // Update invoice in local state
        setInvoices(prev => 
          prev.map(inv => 
            inv.id === selectedInvoice.id 
              ? { ...inv, status: 'PAID', payment_date: new Date().toISOString(), payment_method: paymentForm.method }
              : inv
          )
        );
        toast.success('Paiement effectué avec succès');
      }

      resetPaymentForm();
      setPaymentDialogOpen(false);
      setSelectedInvoice(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors du paiement';
      toast.error(errorMsg);
      console.error('Error processing payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      student_id: '',
      title: '',
      description: '',
      total_amount: '',
      due_date: '',
      periodicity: 'MONTHLY'
    });
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      method: 'CARD',
      card_number: '',
      expiry_date: '',
      cvv: '',
      cardholder_name: '',
      phone_number: ''
    });
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (student && student.first_name) return `${student.first_name} ${student.last_name}`;
    return invoices.find(i => i.student_id === studentId)?.student_name || 'Élève inconnu';
  };

  const getStatusInfo = (status) => {
    return invoiceStatuses.find(s => s.value === status) || invoiceStatuses[0];
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    resetPaymentForm();
    setPaymentDialogOpen(true);
  };

  const canManageBilling = ['ADMIN', 'ACCOUNTANT', 'PRINCIPAL', 'TREASURER'].includes(user.role);
  const canIssueInvoices = user.role === 'ADMIN';
  const canViewInvoices = user.role === 'PARENT' || canManageBilling;
  const canRemind = ['ADMIN', 'TREASURER'].includes(user.role);
  const canImport = ['ADMIN', 'TREASURER', 'PRINCIPAL'].includes(user.role);
  const visibleInvoices = statusFilter === 'ALL' ? invoices : invoices.filter(i => (
    statusFilter === 'PARTIAL'
      ? i.paid_amount > 0 && i.balance > 0 && i.financial_status !== 'CANCELLED'
      : (i.financial_status || i.status) === statusFilter
  ));

  const downloadInvoiceReceipts = async (invoice) => {
    try {
      const { data: pays } = await axios.get(`${API}/invoices/${invoice.id}/payments`);
      if (!pays.length) {
        toast.info('Aucun paiement enregistré dans le système pour cette facture');
        return;
      }
      for (const p of pays) {
        const res = await axios.get(`${API}/payments/${p.id}/receipt`, { responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `recu_${p.receipt_number || p.id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      toast.error('Téléchargement du reçu impossible');
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="billing-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <CreditCard className="w-6 h-6 mr-2 text-blue-600" />
            {user.role === 'PARENT' ? 'Mes Factures' : 'Facturation'}
          </h1>
          <p className="text-gray-600 mt-1">
            {user.role === 'PARENT' 
              ? 'Consultez et payez vos factures scolaires'
              : 'Gestion de la facturation et des paiements'
            }
          </p>
        </div>

        {canIssueInvoices && (
          <>
            <div className="flex space-x-2">
              <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="create-invoice-btn"
                    onClick={() => { resetInvoiceForm(); setInvoiceDialogOpen(true); }}
                    className="btn-primary"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle Facture
                  </Button>
                </DialogTrigger>

            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Créer une facture</DialogTitle>
                <DialogDescription>
                  Générez une nouvelle facture pour un élève
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleInvoiceSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="student">Élève</Label>
                  <Select 
                    value={invoiceForm.student_id} 
                    onValueChange={(value) => setInvoiceForm({ ...invoiceForm, student_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez un élève" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.first_name} {student.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Objet de la facture</Label>
                  <Input
                    id="title"
                    value={invoiceForm.title}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, title: e.target.value })}
                    placeholder="ex: Frais de scolarité T1, Sortie scolaire..."
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (optionnelle)</Label>
                  <Textarea
                    id="description"
                    value={invoiceForm.description}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                    placeholder="Détails de la facturation..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="total_amount">Montant (GNF)</Label>
                    <Input
                      id="total_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={invoiceForm.total_amount}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, total_amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="due_date">Date d'échéance</Label>
                    <Input
                      id="due_date"
                      type="date"
                      value={invoiceForm.due_date}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="periodicity">Périodicité</Label>
                  <Select 
                    value={invoiceForm.periodicity} 
                    onValueChange={(value) => setInvoiceForm({ ...invoiceForm, periodicity: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez la périodicité" />
                    </SelectTrigger>
                    <SelectContent>
                      {periodicityOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setInvoiceDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Création...' : 'Créer la facture'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          <Button 
            data-testid="create-bulk-invoice-btn"
            onClick={() => setBulkInvoiceDialogOpen(true)}
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <Users className="w-4 h-4 mr-2" />
            Factures Groupées
          </Button>
        </div>
        </>
        )}
      </div>

      {/* Statistics */}
      {Object.keys(statistics).length > 0 && canManageBilling && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Factures</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_invoices}</div>
              <p className="text-xs text-gray-600 mt-1">Factures émises</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Payées</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{statistics.paid_invoices}</div>
              <p className="text-xs text-gray-600 mt-1">Factures payées</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">En Attente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{statistics.pending_invoices}</div>
              <p className="text-xs text-gray-600 mt-1">
                Non échues{statistics.pending_validation_invoices > 0 ? ` · ${statistics.pending_validation_invoices} paiement(s) à valider` : ''}
              </p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">En Retard</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{statistics.overdue_invoices}</div>
              <p className="text-xs text-gray-600 mt-1">Factures en retard</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Chiffre d'Affaires</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{Number(statistics.total_revenue || 0).toLocaleString('fr-FR')} GNF</div>
              <p className="text-xs text-gray-600 mt-1">Revenus total</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Taux de Paiement</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-indigo-600">{statistics.payment_rate}%</div>
              <p className="text-xs text-gray-600 mt-1">Factures payées</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content Tabs */}
      {canRemind && <ReminderSettingsCard />}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full ${canManageBilling ? 'grid-cols-4' : 'grid-cols-2'}`}>
          <TabsTrigger value="invoices">Factures</TabsTrigger>
          <TabsTrigger value="payments">Historique Paiements</TabsTrigger>
          {canManageBilling && <TabsTrigger value="collection" data-testid="collection-tab">Recouvrement</TabsTrigger>}
          {canManageBilling && <TabsTrigger value="excel" data-testid="excel-tab">Excel (export / import)</TabsTrigger>}
        </TabsList>

        {canManageBilling && (
          <TabsContent value="collection"><CollectionDashboard /></TabsContent>
        )}

        {canManageBilling && (
          <TabsContent value="excel" className="space-y-6">
            <FinanceExportPanel />
            {canImport && <PaymentsImportPanel onImported={fetchInitialData} />}
          </TabsContent>
        )}

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          {canManageBilling && (
            <div className="flex items-center gap-3">
              <Label className="text-sm text-gray-600">Statut</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-56" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Toutes les factures</SelectItem>
                  <SelectItem value="OVERDUE">En retard</SelectItem>
                  <SelectItem value="PARTIAL">Partiellement payées</SelectItem>
                  <SelectItem value="PENDING">Non échues</SelectItem>
                  <SelectItem value="PAID">Payées</SelectItem>
                  <SelectItem value="CANCELLED">Annulées</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500" data-testid="invoice-filter-count">{visibleInvoices.length} facture(s)</span>
            </div>
          )}
          {visibleInvoices.length > 0 ? (
            <div className="space-y-4">
              {visibleInvoices.map((invoice) => {
                const fstatus = invoice.financial_status || invoice.status;
                const statusInfo = getStatusInfo(fstatus);
                const StatusIcon = statusInfo.icon;
                const isOverdue = fstatus === 'OVERDUE';
                const canPay = ['PENDING', 'PARTIAL', 'OVERDUE'].includes(fstatus) && !invoice.pending_validation;

                return (
                  <Card key={invoice.id} className="card">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-lg">{invoice.title}</h3>
                            <Badge className={statusInfo.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusInfo.label}
                            </Badge>
                            {invoice.paid_amount > 0 && fstatus !== 'PAID' && (
                              <Badge className="badge-warning" data-testid={`invoice-balance-${invoice.id}`}>
                                Reste {Number(invoice.balance).toLocaleString('fr-FR')} GNF
                              </Badge>
                            )}
                            {invoice.pending_validation && (
                              <Badge className="bg-sky-100 text-sky-800" data-testid={`invoice-pending-validation-${invoice.id}`}>
                                Paiement déclaré, en attente de validation
                              </Badge>
                            )}
                            {canManageBilling && invoice.reminder_count > 0 && (
                              <Badge className="bg-orange-100 text-orange-800" data-testid={`invoice-reminder-count-${invoice.id}`}>
                                <BellRing className="w-3 h-3 mr-1" />
                                Relancée {invoice.reminder_count}× · {new Date(invoice.last_reminder_at).toLocaleDateString('fr-FR')}
                              </Badge>
                            )}
                          </div>

                          {canManageBilling && (
                            <p className="text-sm text-gray-600 mb-2">
                              Élève: {getStudentName(invoice.student_id)}
                            </p>
                          )}

                          <p className="text-sm text-gray-600 mb-2">
                            N° Facture: {invoice.invoice_number}
                          </p>

                          {invoice.description && (
                            <p className="text-sm text-gray-700 mb-3">
                              {invoice.description}
                            </p>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="text-2xl font-bold text-blue-600" data-testid={`invoice-amount-${invoice.id}`}>
                              {(fstatus !== 'PAID' && fstatus !== 'CANCELLED' && invoice.balance != null ? invoice.balance : invoice.total_amount).toLocaleString('fr-FR')} GNF
                              {invoice.paid_amount > 0 && fstatus !== 'PAID' && (
                                <span className="block text-xs font-normal text-gray-500">reste à payer sur {invoice.total_amount.toLocaleString('fr-FR')} GNF</span>
                              )}
                            </div>

                            <div className="text-right">
                              <div className="text-sm text-gray-600">
                                Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-FR')}
                              </div>
                              {invoice.payment_date && (
                                <div className="text-sm text-green-600">
                                  Payée le {new Date(invoice.payment_date).toLocaleDateString('fr-FR')}
                                </div>
                              )}
                            </div>
                          </div>

                          {canPay && user.role === 'PARENT' && (
                            <div className="mt-4 flex space-x-3">
                              <Button
                                onClick={() => openPaymentDialog(invoice)}
                                className="btn-success"
                                size="sm"
                                data-testid={`pay-invoice-${invoice.id}`}
                              >
                                <CreditCard className="w-4 h-4 mr-2" />
                                Payer maintenant
                              </Button>
                            </div>
                          )}

                          {canRemind && canPay && (
                            <div className="mt-4">
                              <Button variant="outline" size="sm" className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                data-testid={`remind-invoice-${invoice.id}`} onClick={() => setReminderInvoice(invoice)}>
                                <BellRing className="w-4 h-4 mr-2" />
                                Relancer
                              </Button>
                            </div>
                          )}

                          {invoice.has_payments && (
                            <div className="mt-4">
                              <Button variant="outline" size="sm" data-testid={`download-receipts-${invoice.id}`} onClick={() => downloadInvoiceReceipts(invoice)}>
                                <Download className="w-4 h-4 mr-2" />
                                Télécharger reçu
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune facture</h3>
              <p className="text-gray-600">
                {user.role === 'PARENT' 
                  ? 'Vous n\'avez aucune facture en attente.'
                  : 'Aucune facture n\'a été créée.'
                }
              </p>
            </div>
          )}
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Historique des Paiements</CardTitle>
              <CardDescription>Liste de tous les paiements enregistrés</CardDescription>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun paiement</h3>
                  <p className="text-gray-600">
                    Aucun paiement n'a encore été enregistré
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Élève
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Montant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Méthode
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Référence
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payments.map((payment) => {
                        const student = students.find(s => s.id === payment.student_id);
                        const paymentDate = payment.created_at ? new Date(payment.created_at).toLocaleDateString('fr-FR') : '-';
                        const paymentMethod = payment.payment_method || 'N/A';
                        
                        return (
                          <tr key={payment.payment_id || payment.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {paymentDate}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.student_name || (student ? `${student.first_name} ${student.last_name}` : 'N/A')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {payment.amount ? payment.amount.toLocaleString('fr-FR') : '0'} GNF
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {paymentMethod === 'CASH' && '💵 Espèces'}
                              {paymentMethod === 'CHECK' && '📝 Chèque'}
                              {paymentMethod === 'BANK_TRANSFER' && '🏦 Virement'}
                              {paymentMethod === 'ORANGE_MONEY' && '🍊 Orange Money'}
                              {paymentMethod === 'CARD' && '💳 Carte'}
                              {!['CASH', 'CHECK', 'BANK_TRANSFER', 'ORANGE_MONEY', 'CARD'].includes(paymentMethod) && paymentMethod}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {payment.reference || payment.transaction_id || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge className={payment.status === 'SUCCESSFUL' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                {payment.status === 'SUCCESSFUL' ? 'Réussi' : payment.status}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog - New Multi-Method Payment */}
      <ParentPaymentDialog
        invoice={selectedInvoice}
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setSelectedInvoice(null);
        }}
        onPaymentSubmitted={() => {
          fetchInitialData();
          toast.success('Paiement enregistré avec succès!');
        }}
      />
      
      <BulkInvoiceDialog
        open={bulkInvoiceDialogOpen}
        onClose={() => setBulkInvoiceDialogOpen(false)}
        onSuccess={() => {
          fetchInitialData();
          toast.success('Factures créées avec succès!');
        }}
      />

      <InvoiceReminderDialog
        invoice={reminderInvoice}
        open={!!reminderInvoice}
        onClose={() => setReminderInvoice(null)}
        onSent={fetchInitialData}
      />
    </div>
  );
};

export default BillingManagement;